import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Icon } from '@/shared/icons';
import type { IconName } from '@/shared/icons';

import { SidebarDropdown } from './SidebarDropdown';
import { menuGroupLabelAt, startsMenuGroup } from './menu-groups';
import { usePermissions } from '@/hooks/usePermissions';
import type { AccessRule } from '@/lib/route-access';
import { useAuth } from '@/contexts/AuthContext';
import { useApp } from '@/contexts/AppContext';
import { useTheme } from '@/contexts/theme';
import { useCompanyDrawer } from '@/contexts/CompanyDrawerContext';
import { SidebarItem } from '@/types/navigation';
import { HEADER_HEIGHT } from '@/shared/constants/layout';
import { useUnreadCount } from '@/modules/chat/hooks';
import { CompanyEditDrawer } from '@/modules/settings/company/CompanyEditDrawer';
// Funções absorvidas do Header (F3): utilidades do rodapé + bloco de conta
import { useEmailUnreadCount } from './header/useEmailUnreadCount';
import { NotificationsDropdown } from './header/NotificationsDropdown';
import { ActivitiesDropdown } from './header/ActivitiesDropdown';
import { TeamChatButton } from '@/modules/team-chat/components/TeamChatButton';
import { UserProfileDropdown } from './header/UserProfileDropdown';
import { CachedAvatar } from '@/shared/components/ui/cached-avatar';
import { AllSessionsDialog } from '@/shared/components/dialogs/AllSessionsDialog';
import { AccountMenuContent, useAccountSessions } from './header/AccountMenuContent';

/**
 * Quem vê cada item do menu (20/10/2026): a MESMA regra da trava das telas,
 * em `lib/route-access.ts`.
 *
 * - Item com `href` aparece se a pessoa pode abrir aquele endereço. Endereço
 *   sem regra na tabela é negado — por isso os itens de telas que não existem
 *   (Vendas, Chamadas, Base de Conhecimento) não aparecem.
 * - Grupo (item com filhos) aparece se ao menos um filho aparece.
 * - Item sem `href` (abre gaveta) precisa de regra própria, abaixo.
 *
 * Antes havia aqui um mapa próprio por id de item, que divergia da trava e
 * dos outros menus — e item novo sem entrada ficava visível para todos (o
 * buraco se repetiu quatro vezes).
 */
const ITEMS_WITHOUT_ROUTE: Record<string, AccessRule> = {
  empresa: { module: 'company', action: 'read' },
};

/**
 * Seções de navegação com labels para agrupamento visual
 */
interface MenuSection {
  label: string;
  items: SidebarItem[];
}

const menuSections: MenuSection[] = [
  {
    label: '',
    items: [
      {
        id: 'dashboards',
        label: 'Dashboards',
        href: '/',
        iconName: 'pie-chart',
        children: [
          {
            id: 'dashboards-support',
            label: 'Atendimento',
            href: '/dashboard/management/support',
            iconName: 'headphones',
            group: 'gestao',
          },
          {
            id: 'dashboards-commercial',
            label: 'Gestão Comercial',
            href: '/dashboard/management/commercial',
            iconName: 'bar-chart',
            group: 'gestao',
          },
          {
            id: 'dashboards-marketing',
            label: 'Marketing',
            href: '/dashboard/management/marketing',
            iconName: 'target',
            group: 'gestao',
          },
          {
            id: 'dashboards-analytics',
            label: 'Analytics',
            href: '/dashboard/management/analytics',
            iconName: 'activity',
            group: 'gestao',
          },
          // Bloco "Usuário": números do PRÓPRIO usuário, não da empresa.
          // "Minhas Vendas" e não "Vendas" porque já existe um menu "Vendas"
          // no topo (Produtos/Pedidos/Catálogo) — dois itens com o mesmo nome
          // viram pergunta toda vez que alguém diz "abre o Vendas".
          {
            id: 'crm-seller',
            label: 'Minhas Vendas',
            href: '/dashboard/management/sales',
            iconName: 'award',
            group: 'usuario',
          },
        ],
      },
      {
        id: 'atendimento',
        label: 'Atendimento',
        href: '/chat/management',
        iconName: 'message-square',
      },
      {
        id: 'agenda',
        label: 'Agenda',
        href: '/calendar',
        iconName: 'calendar',
      },
      {
        id: 'crm',
        label: 'CRM',
        href: '/crm',
        iconName: 'trending-up',
        children: [
          { id: 'pipelines', label: 'Pipelines', href: '/crm', iconName: 'layers' },
          { id: 'leads-list', label: 'Leads', href: '/crm/leads', iconName: 'users' },
          { id: 'clientes', label: 'Clientes', href: '/crm/customers', iconName: 'briefcase' },
          { id: 'crm-ranking', label: 'Ranking', href: '/crm/ranking', iconName: 'star' },
        ],
      },
      {
        id: 'vendas',
        label: 'Vendas',
        href: '/sales',
        iconName: 'shopping-bag',
        children: [
          { id: 'produtos', label: 'Produtos', href: '/sales/products', iconName: 'package' },
          { id: 'pedidos', label: 'Pedidos', href: '/sales/orders', iconName: 'shopping-bag' },
          { id: 'catalogo', label: 'Catálogo', href: '/sales/catalog', iconName: 'grid' },
        ],
      },
      {
        id: 'helpdesk',
        label: 'Help Desk',
        href: '/helpdesk',
        iconName: 'headphones',
        children: [
          { id: 'tickets', label: 'Tickets', href: '/helpdesk/tickets', iconName: 'flag' },
          { id: 'projetos', label: 'Projetos', href: '/helpdesk/projects', iconName: 'folder' },
          { id: 'conhecimento', label: 'Base de Conhecimento', href: '/helpdesk/knowledge', iconName: 'help-circle' },
          // Filho COM filhos — primeiro submenu dentro de submenu do shell.
          // Não abre um 3º dropdown: o painel do flyout (desktop) / da gaveta
          // (mobile) DESLIZA para o submenu, com "voltar" no topo.
          {
            id: 'checklists',
            label: 'Checklists',
            href: '/checklists',
            iconName: 'check-square',
            children: [
              { id: 'checklists-list', label: 'Aplicações', href: '/checklists', iconName: 'check-square' },
              { id: 'checklists-my', label: 'Meus checklists', href: '/checklists/my', iconName: 'user-check' },
              { id: 'checklists-templates', label: 'Modelos', href: '/checklists/templates', iconName: 'file-text' },
            ],
          },
        ],
      },
      {
        id: 'chamadas',
        label: 'Chamadas',
        href: '/calls',
        iconName: 'phone',
      },
      {
        id: 'automacao',
        label: 'Automação',
        href: '/automation',
        iconName: 'zap',
        children: [
          { id: 'agentes-ia', label: 'Agentes de IA', href: '/automation/ai-agents', iconName: 'cpu' },
          { id: 'workflows', label: 'Workflows', href: '/automation/workflows', iconName: 'git-branch' },
          { id: 'skills', label: 'Skills', href: '/automation/skills', iconName: 'zap' },
          { id: 'ferramentas', label: 'Ferramentas', href: '/automation/custom-actions', iconName: 'tool' },
          { id: 'vozes', label: 'Vozes', href: '/automation/voices', iconName: 'mic' },
          { id: 'formularios', label: 'Formulários', href: '/automation/forms', iconName: 'file-text' },
        ],
      },
      {
        id: 'prospeccao',
        label: 'Prospecção',
        href: '/prospecting/campaigns',
        iconName: 'target',
        children: [
          { id: 'campanhas-prospeccao', label: 'Campanhas', href: '/prospecting/campaigns', iconName: 'send' },
          { id: 'templates-prospeccao', label: 'Templates', href: '/prospecting/templates', iconName: 'file-text' },
          { id: 'audiences-prospeccao', label: 'Públicos', href: '/prospecting/audiences', iconName: 'list' },
        ],
      },
      {
        id: 'relatorios',
        label: 'Relatórios',
        href: '/reports',
        iconName: 'bar-chart',
        children: [
          { id: 'vendas-report', label: 'Vendas', href: '/reports/sales', iconName: 'shopping-bag' },
          { id: 'suporte-report', label: 'Atendimento', href: '/reports/support', iconName: 'headphones' },
          { id: 'crm-report', label: 'CRM', href: '/reports/crm', iconName: 'trending-up' },
          { id: 'csat-report', label: 'CSAT', href: '/reports/csat', iconName: 'star' },
        ],
      },
      {
        id: 'databases',
        label: 'Bases de dados',
        href: '/databases',
        iconName: 'database',
      },
      {
        id: 'websites',
        label: 'Websites',
        href: '/websites',
        iconName: 'globe',
      },
    ],
  },
];

/**
 * Menu Configurações. A ordem segue a CADEIA DE DEPENDÊNCIA das telas — o que
 * precisa existir antes vem antes — e o `group` de cada item vira uma linha
 * separadora entre blocos (desktop e gaveta mobile). A cadeia, conferida nos
 * schemas do backend e não estimada:
 *
 *   organizacao → Empresa é a raiz (não depende de nada). Unidade depende só
 *                 dela: `createUnitSchema` exige apenas `name`, e gerente/membros
 *                 entram depois via `unit-users`.
 *   acesso      → `users.levelId` obriga Níveis antes de Usuários; Equipe agrupa usuários.
 *   escalas     → Turno (`userId`/`teamId`) e Meta (alvo `user|team|company`)
 *                 apontam para o bloco anterior.
 *   atendimento → canal de e-mail consulta `email-domains` (channels/repository.ts),
 *                 e política de SLA aponta para `teamIds`/`channelIds`/`pipelineIds`.
 */
const buildSettingsItem = (onOpenCompanyDrawer: () => void): SidebarItem => ({
  id: 'configuracoes',
  label: 'Configurações',
  href: '/settings',
  iconName: 'settings',
  children: [
    { id: 'empresa', label: 'Empresa', iconName: 'briefcase', onClick: onOpenCompanyDrawer, group: 'organizacao' },
    { id: 'unidades', label: 'Unidades', href: '/settings/units', iconName: 'map-pin', group: 'organizacao' },
    { id: 'tags', label: 'Tags', href: '/settings/tags', iconName: 'tag', group: 'organizacao' },
    { id: 'niveis-acesso', label: 'Níveis de Acesso', href: '/settings/levels', iconName: 'shield', group: 'acesso' },
    { id: 'usuarios', label: 'Usuários', href: '/settings/users', iconName: 'users', group: 'acesso' },
    { id: 'equipes', label: 'Equipes', href: '/settings/teams', iconName: 'users', group: 'acesso' },
    { id: 'turnos', label: 'Turnos', href: '/settings/shifts', iconName: 'clock', group: 'escalas' },
    { id: 'metas', label: 'Metas', href: '/settings/goals', iconName: 'target', group: 'escalas' },
    { id: 'gamificacao', label: 'Gamificação', href: '/settings/gamification', iconName: 'award', group: 'escalas' },
    { id: 'dominios-email', label: 'Domínios de E-mail', href: '/settings/email-domains', iconName: 'mail', group: 'atendimento' },
    { id: 'canais-config', label: 'Canais de Atendimento', href: '/settings/channels', iconName: 'message-circle', group: 'atendimento' },
    { id: 'politicas-sla', label: 'Políticas de SLA', href: '/settings/sla-policies', iconName: 'clock', group: 'atendimento' },
  ],
});

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  /** Alterna expandido ↔ rail (colapsar) no DESKTOP. Vem do MainLayout. */
  onToggle?: () => void;
  /** Drawer mobile aberto (estado efêmero do MainLayout, desacoplado do rail desktop). */
  mobileOpen?: boolean;
  /** Fecha o drawer mobile (scrim). */
  onMobileClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle, mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canViewDashboard, canAccessPath, can } = usePermissions();
  const { state, savedSessions } = useAuth();
  const { app } = useApp();
  const { theme, toggleTheme } = useTheme();
  // Hook para contagem de mensagens não lidas
  const { data: totalUnreadCount = 0 } = useUnreadCount();
  // Badge de e-mail não lido (absorvido do Header)
  const { data: emailUnreadCount = 0 } = useEmailUnreadCount();

  // CONTEÚDO expandido = rail desktop aberto OU drawer mobile aberto. O `isOpen`
  // (pref de rail desktop) controla só a LARGURA no lg; o conteúdo (labels) segue
  // `expanded`, senão o drawer mobile mostraria ícones-só numa gaveta de 256px.
  const expanded = isOpen || mobileOpen;

  // Abertura da gaveta de Empresa vive em contexto: o mesmo item existe em TRÊS
  // lugares (menu Configurações, sub-tela de conta do drawer mobile e busca ⌘K)
  // e o estado precisa ser um só.
  const { isOpen: companyDrawerOpen, open: openCompanyDrawer, close: closeCompanyDrawer } = useCompanyDrawer();

  // AllSessionsDialog da sub-tela de conta do drawer mobile
  const [showAllSessions, setShowAllSessions] = useState(false);
  const { otherSessions, switchTo, removeSession } = useAccountSessions();

  const settingsItem = useMemo(
    () => buildSettingsItem(openCompanyDrawer),
    [openCompanyDrawer]
  );

  // Tooltip state (portal). `dir`: 'right' no rail (colapsado), 'up' no expandido.
  const [tooltip, setTooltip] = useState<{ label: string; top: number; left: number; dir: 'right' | 'up' | 'down' } | null>(null);

  // Dropdown state — only one open at a time
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const settingsTriggerRef = useRef<HTMLDivElement>(null);

  // Drawer mobile: PILHA de submenus abertos nas sub-telas deslizantes
  // (anatomia própria do mockup — NÃO o flyout do desktop). Vazio = tela
  // principal. Pilha (e não um id só) porque o mecanismo é GENÉRICO: qualquer
  // filho-com-filhos desliza mais um nível (hoje Help Desk → Checklists).
  // Item sintético `{ id: 'account' }` = sub-tela de conta.
  const [mobileStack, setMobileStack] = useState<SidebarItem[]>([]);
  // Pilha MONTADA — encolhe só DEPOIS da transição. Sem isso, a tela que sai
  // desmontaria antes de deslizar e o "voltar" revelaria um vão em branco.
  const [mobileRenderStack, setMobileRenderStack] = useState<SidebarItem[]>([]);
  useEffect(() => {
    if (mobileStack.length >= mobileRenderStack.length) {
      setMobileRenderStack(mobileStack);
      return;
    }
    const timer = window.setTimeout(() => setMobileRenderStack(mobileStack), 280);
    return () => window.clearTimeout(timer);
  }, [mobileStack, mobileRenderStack.length]);
  // Reset da sub-tela sempre que o drawer fecha (evita reabrir já dentro do submenu)
  React.useEffect(() => {
    if (!mobileOpen) setMobileStack([]);
  }, [mobileOpen]);

  const toggleDropdown = useCallback((itemId: string) => {
    setOpenDropdownId(prev => prev === itemId ? null : itemId);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpenDropdownId(null);
  }, []);

  const showTooltip = (e: React.MouseEvent, label: string) => {
    const rect = e.currentTarget.getBoundingClientRect();
    // Expandido (toolbar no topo) → ABAIXO, alinhado à esquerda do ícone (cresce pra dentro, não corta); rail → à DIREITA.
    if (expanded) {
      setTooltip({ label, dir: 'down', top: rect.bottom + 8, left: rect.left });
    } else {
      setTooltip({ label, dir: 'right', top: rect.top + rect.height / 2, left: rect.right + 8 });
    }
  };

  const hideTooltip = () => setTooltip(null);

  // Nenhum tooltip sobrevive a um clique (qualquer lugar). Ex.: clicar "Expandir menu"
  // não pode deixar o tooltip preso. Captura na fase de captura, antes do onClick.
  useEffect(() => {
    const clear = () => setTooltip(null);
    document.addEventListener('pointerdown', clear, true);
    return () => document.removeEventListener('pointerdown', clear, true);
  }, []);

  // Detecta se está em localhost (desenvolvimento)
  const isLocalhost = window.location.hostname === 'localhost' ||
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '';

  // IDs dos menus e submenus que aparecem apenas em desenvolvimento.
  // vendas-report/suporte-report permanecem aqui: as rotas /reports/sales e
  // /reports/support ainda não existem no router (dariam 404 em produção).
  const developmentOnlyMenuIds = ['vendas-report', 'suporte-report'];

  /**
   * O item aparece? Grupo: decidido pelos filhos (abaixo). Folha: pela regra
   * do endereço. Item sem endereço: pela regra própria.
   */
  const isItemVisible = (item: SidebarItem): boolean => {
    if (item.children && item.children.length > 0) return true;
    const ownRule = ITEMS_WITHOUT_ROUTE[item.id];
    if (ownRule) return can(ownRule);
    return item.href ? canAccessPath(item.href) : false;
  };

  /**
   * Filtra itens do menu baseado em permissões e ambiente
   */
  const filterMenuItemsByPermissions = (items: SidebarItem[]): SidebarItem[] => {
    return items
      .filter(item => {
        // Filtra menus de desenvolvimento se não estiver em localhost
        if (!isLocalhost && developmentOnlyMenuIds.includes(item.id)) {
          return false;
        }

        return isItemVisible(item);
      })
      .map(item => {
        // Se o item tem filhos, filtra recursivamente
        if (item.children) {
          const filteredChildren = filterMenuItemsByPermissions(item.children);

          // Se não há filhos visíveis, não mostra o pai
          if (filteredChildren.length === 0) {
            return null;
          }

          return {
            ...item,
            children: filteredChildren
          };
        }

        return item;
      })
      .filter((item): item is SidebarItem => item !== null);
  };

  /**
   * Seções filtradas com permissões
   */
  const filteredSections = useMemo(
    () => menuSections.map(section => ({
      ...section,
      items: filterMenuItemsByPermissions(section.items),
    })).filter(section => section.items.length > 0),
    [state.user?.permissions]
  );

  /**
   * Settings item filtrado com permissões
   */
  const filteredSettingsItem = useMemo(() => {
    if (settingsItem.children) {
      const filteredChildren = filterMenuItemsByPermissions(settingsItem.children);
      if (filteredChildren.length === 0) return null;
      return { ...settingsItem, children: filteredChildren };
    }
    return settingsItem;
  }, [state.user?.permissions]);

  const isItemActive = (item: SidebarItem): boolean => {
    if (item.href === '/' && location.pathname === '/') return true;
    if (item.href && item.href !== '/' && location.pathname.startsWith(item.href)) return true;
    // Grupo herda o "ativo" de qualquer DESCENDENTE. Virou necessário quando
    // 'checklists' passou a ser filho de 'helpdesk': `/checklists/*` não é
    // prefixo do href do pai (`/helpdesk`), então NENHUM item da sidebar
    // destacava nessas rotas. Grupos cujos filhos já vivem sob o prefixo do pai
    // (CRM, Vendas, Automação, Relatórios, Configurações) não mudam — a
    // recursão só reconfirma o que o `startsWith` acima já dizia. Dashboards e
    // Prospecção passam a destacar em sub-rotas onde antes nada destacava.
    if (item.children?.some((child) => isItemActive(child))) return true;
    return false;
  };

  const menuTriggerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const renderMenuItem = (item: SidebarItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = isItemActive(item);
    const isDropdownOpen = openDropdownId === item.id;
    const isCompact = !expanded;
    const isDevelopmentMenu = isLocalhost && developmentOnlyMenuIds.includes(item.id);

    // Sub-items are no longer rendered inline — skip level > 0
    if (level > 0) {
      return null;
    }

    return (
      <div key={item.label}>
        <div
          data-click={item.id}
          data-testid={`sidebar-item-${item.id}`}
          ref={hasChildren ? (el: HTMLDivElement | null) => { menuTriggerRefs.current[item.id] = el; } : undefined}
          className={clsx(
            'flex items-center text-sm rounded-lg cursor-pointer transition-all duration-200 group relative',
            (isActive || isDropdownOpen) ? 'font-semibold' : 'font-medium',
            {
              'h-[38px] px-3': !isCompact,
              'w-[32px] h-[32px] mx-auto justify-center': isCompact,
            }
          )}
          style={{
            backgroundColor: (isActive || isDropdownOpen)
              ? 'var(--theme-sidebar-item-active-bg)'
              : 'transparent',
            color: (isActive || isDropdownOpen)
              ? 'var(--theme-sidebar-item-active-text)'
              : 'var(--theme-sidebar-text)',
            ...(isDevelopmentMenu && {
              border: '2px solid var(--theme-warning-color)',
              borderRadius: '0.5rem'
            })
          }}
          onMouseEnter={(e) => {
            if (isCompact && !isDropdownOpen) showTooltip(e, item.label);
            if (!isActive && !isDropdownOpen) {
              // hover ≠ active: fundo neutro sutil, não o preenchimento sólido
              e.currentTarget.style.color = 'var(--theme-sidebar-text-hover)';
              e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
            }
          }}
          onMouseLeave={(e) => {
            if (isCompact) hideTooltip();
            if (!isActive && !isDropdownOpen) {
              e.currentTarget.style.color = 'var(--theme-sidebar-text)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          onClick={() => {
            hideTooltip();
            if (hasChildren) {
              toggleDropdown(item.id);
            }
          }}
        >
          {item.iconName && (
            <Icon
              name={item.iconName as IconName}
              className={clsx('h-[18px] w-[18px] flex-shrink-0', {
                'mr-3': !isCompact,
              })}
            />
          )}

          {!isCompact && (
            <>
              {item.href && !hasChildren ? (
                <Link to={item.href} className="flex-1">
                  {item.label}
                </Link>
              ) : (
                <span className="flex-1">{item.label}</span>
              )}

              {item.badge && (
                /* F69: tokens do tema (nunca paleta Tailwind literal) — badge segue dark mode */
                <span
                  className="ml-auto text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: 'var(--theme-primary-background)',
                    color: 'var(--theme-primary)',
                  }}
                >
                  {item.badge}
                </span>
              )}

              {hasChildren && (
                <div className="ml-auto">
                  <Icon name="chevron-right" className="h-4 w-4" style={{ opacity: 0.5 }} />
                </div>
              )}
            </>
          )}

          {/* Link wrapper for compact mode */}
          {isCompact && item.href && !hasChildren && (
            <Link to={item.href} className="absolute inset-0" />
          )}
        </div>

        {/* Flyout dropdown for items with children */}
        {hasChildren && item.children && (
          <SidebarDropdown
            items={item.children}
            title={item.label}
            direction="down"
            triggerEl={menuTriggerRefs.current[item.id] ?? null}
            isOpen={isDropdownOpen}
            onClose={closeDropdown}
          />
        )}
      </div>
    );
  };

  /**
   * Renderiza um label de seção
   */
  const renderSectionLabel = (label: string) => {
    if (!label) return null;
    if (!expanded) {
      // Compact mode: render a small spacer + subtle divider between sections
      return (
        <div
          className="my-1.5 mx-2"
          style={{
            borderTop: '1px solid var(--theme-sidebar-border)',
          }}
        />
      );
    }
    return (
      <div
        className="px-3 pt-4 pb-1"
      >
        <span
          className="text-[11px] font-semibold tracking-wider"
          style={{ color: 'var(--theme-muted-text)', opacity: 0.6 }}
        >
          {label}
        </span>
      </div>
    );
  };

  /**
   * Drawer MOBILE (mockup app.html `.m-drawer`) — anatomia própria:
   * gaveta 84%/máx 330px, sub-tela deslizante (in-place) com seta voltar,
   * itens 46px, rodapé com bloco de conta. Reusa os mesmos dados já filtrados
   * por permissão (`filteredSections` + `filteredSettingsItem`) — zero duplicação.
   */
  const renderMobileDrawer = () => {
    // Lista da tela principal = seções + Configurações como último item.
    const mainItems: SidebarItem[] = [
      ...filteredSections.flatMap((s) => s.items),
      ...(filteredSettingsItem ? [filteredSettingsItem] : []),
    ];
    // Profundidade LÓGICA (o quanto a esteira está deslocada) — a pilha MONTADA
    // (`mobileRenderStack`) pode estar um nível à frente durante o "voltar".
    const depth = mobileStack.length;

    const go = (href?: string, onClick?: () => void) => {
      onMobileClose?.();
      setMobileStack([]);
      if (onClick) onClick();
      else if (href) navigate(href);
    };

    const MDItem: React.FC<{ item: SidebarItem }> = ({ item }) => {
      const hasChildren = !!item.children && item.children.length > 0;
      const active = isItemActive(item);
      return (
        <button
          type="button"
          data-testid={`mobile-drawer-item-${item.id}`}
          onClick={() => (hasChildren ? setMobileStack((prev) => [...prev, item]) : go(item.href, item.onClick))}
          className="flex items-center w-full text-left"
          style={{
            height: 46,
            padding: '0 12px',
            gap: 13,
            borderRadius: 10,
            fontSize: 15,
            fontWeight: active ? 600 : 500,
            color: active ? 'var(--theme-sidebar-item-active-text)' : 'var(--theme-sidebar-text)',
            backgroundColor: active ? 'var(--theme-sidebar-item-active-bg)' : 'transparent',
          }}
        >
          {item.iconName && (
            <Icon
              name={item.iconName as IconName}
              className="h-[20px] w-[20px] flex-shrink-0"
              style={{ color: active ? 'var(--theme-sidebar-item-active-text)' : 'var(--theme-muted-text)' }}
            />
          )}
          <span className="flex-1 min-w-0 truncate">{item.label}</span>
          {hasChildren && (
            <Icon name="chevron-right" className="h-4 w-4 flex-shrink-0" style={{ opacity: 0.45 }} />
          )}
        </button>
      );
    };

    return (
      <div className="min-[900px]:hidden" aria-hidden={!mobileOpen}>
        {/* Scrim */}
        <div
          className="fixed inset-0 z-[59] transition-opacity duration-200"
          style={{
            backgroundColor: 'rgba(15,23,42,.42)',
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? 'auto' : 'none',
          }}
          onClick={onMobileClose}
        />
        {/* Gaveta */}
        <aside
          data-testid="mobile-sidebar-drawer"
          className="fixed top-0 bottom-0 left-0 z-[60] flex flex-col overflow-hidden"
          style={{
            width: '84%',
            maxWidth: 330,
            borderRadius: '0 20px 20px 0',
            // Overlay sobre conteúdo: tint da sidebar sobre base opaca (temas dark
            // com imagem têm --theme-sidebar-background translúcida e vazariam)
            background: 'var(--theme-overlay-sidebar-background)',
            transform: mobileOpen ? 'none' : 'translateX(-102%)',
            transition: 'transform .24s ease',
          }}
        >
          {/* Track deslizante: cada tela ocupa 100% da gaveta (flex-none w-full) e
              a esteira anda -100% por nível — suporta profundidade arbitrária.
              A transição respeita `prefers-reduced-motion` pela regra global do
              index.css (transition-duration: .01ms). */}
          <div
            className="flex h-full"
            style={{
              width: '100%',
              transform: depth > 0 ? `translateX(-${depth * 100}%)` : 'none',
              transition: 'transform .26s ease',
            }}
          >
            {/* TELA 1 — nav principal */}
            <div className="flex flex-col flex-none w-full min-w-0">
              {/* Topo: logo + workspace */}
              <div className="flex items-center gap-3" style={{ padding: '16px 16px 10px' }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 34, height: 34, borderRadius: 10, overflow: 'hidden' }}>
                  {app?.logo ? (
                    <img src={app.logo} alt={app.name || 'Logo'} className="w-full h-full object-contain" />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: 'var(--theme-primary)', color: 'var(--theme-sidebar-active-text)' }}>
                      {app?.name?.charAt(0)?.toUpperCase() || 'T'}
                    </span>
                  )}
                </span>
                <div className="min-w-0 leading-tight">
                  <div className="font-bold truncate" style={{ color: 'var(--theme-text)', fontSize: 15 }}>{app?.name || 'Troia'}</div>
                  <div className="truncate" style={{ color: 'var(--theme-muted-text)', fontSize: 12 }}>{state.user?.firstName ? `${state.user.firstName}${state.user.lastName ? ' ' + state.user.lastName : ''}` : 'Espaço de trabalho'}</div>
                </div>
              </div>
              {/* Nav */}
              <nav className="flex-1 overflow-y-auto" style={{ padding: '4px 10px 10px' }}>
                {mainItems.map((item) => <MDItem key={item.id} item={item} />)}
              </nav>
              {/* Rodapé: bloco de conta — abre a sub-tela de conta (mesma transição dos submenus) */}
              <div style={{ borderTop: '1px solid var(--theme-sidebar-border)', padding: '8px 10px' }}>
                <button
                  type="button"
                  data-testid="mobile-drawer-account"
                  onClick={() => setMobileStack([{ id: 'account', label: 'Minha conta' }])}
                  className="flex items-center w-full text-left space-x-3 rounded-[10px] px-2 py-[7px]"
                  style={{ color: 'var(--theme-sidebar-text)' }}
                >
                  <CachedAvatar
                    src={state.user?.avatar}
                    alt={state.user?.firstName || 'Usuário'}
                    size="md"
                    isSquircle={true}
                  />
                  <div className="text-left flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-tight truncate" style={{ color: 'var(--theme-sidebar-text-hover)' }}>
                      {state.user ? [state.user.firstName, state.user.lastName].filter(Boolean).join(' ') : 'Usuário'}
                    </div>
                    <div className="text-xs opacity-75 truncate" style={{ color: 'var(--theme-sidebar-text)' }}>
                      {state.user?.email || ''}
                    </div>
                  </div>
                  <Icon name="chevron-right" className="h-4 w-4 flex-shrink-0" style={{ opacity: 0.45 }} />
                </button>
              </div>
            </div>

            {/* TELAS 2..N — uma por nível da pilha (submenus + sub-tela de conta) */}
            {mobileRenderStack.map((screen, index) => (
              <div key={screen.id} className="flex flex-col flex-none w-full min-w-0">
                <div className="flex items-center gap-2" style={{ padding: '16px 12px 10px' }}>
                  <button
                    type="button"
                    onClick={() => setMobileStack((prev) => prev.slice(0, index))}
                    data-testid={index === 0 ? 'mobile-drawer-back' : `mobile-drawer-back-${index}`}
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: 34, height: 34, borderRadius: 10, color: 'var(--theme-text)' }}
                    aria-label="Voltar"
                  >
                    <Icon name="chevron-left" className="h-[22px] w-[22px]" />
                  </button>
                  <span className="font-bold truncate" style={{ color: 'var(--theme-text)', fontSize: 16 }}>{screen.label}</span>
                </div>
                <nav className="flex-1 overflow-y-auto" style={{ padding: '4px 10px 10px' }}>
                  {screen.id === 'account' ? (
                    <AccountMenuContent
                      variant="drawer"
                      onClose={() => { onMobileClose?.(); setMobileStack([]); }}
                      onOpenAllSessions={() => setShowAllSessions(true)}
                      onOpenCompanyDrawer={openCompanyDrawer}
                    />
                  ) : (
                    screen.children?.map((child, childIndex) => {
                      const list = screen.children ?? [];
                      const groupLabel = menuGroupLabelAt(list, childIndex);
                      return (
                        <React.Fragment key={child.id}>
                          {/* Bloco COM nome mostra o nome; bloco sem nome
                              continua só com a linha. O título da TELA (ao lado
                              do voltar) permanece o nome do menu — é a âncora da
                              navegação: tocar "Dashboards" e cair numa tela
                              chamada "Gestão" faria o usuário achar que errou o
                              caminho. */}
                          {groupLabel !== null ? (
                            <div
                              className="px-3 py-2 mb-1 font-semibold uppercase"
                              style={{
                                color: 'var(--theme-muted-text)',
                                fontSize: 11,
                                letterSpacing: '0.05em',
                              }}
                              data-testid={`mobile-drawer-group-label-${child.group ?? ''}`}
                            >
                              {groupLabel}
                            </div>
                          ) : (
                            startsMenuGroup(list, childIndex) && (
                              <div
                                role="separator"
                                data-testid={`mobile-drawer-separator-${child.id}`}
                                style={{
                                  height: 1,
                                  margin: '6px 12px',
                                  backgroundColor: 'var(--theme-sidebar-border)',
                                }}
                              />
                            )
                          )}
                          <MDItem item={child} />
                        </React.Fragment>
                      );
                    })
                  )}
                </nav>
              </div>
            ))}
          </div>
        </aside>
      </div>
    );
  };

  if (!canViewDashboard()) {
    return null;
  }

  return (
    <>
      {renderMobileDrawer()}

      {/* Sidebar DESKTOP (rail) — mobile usa o drawer próprio acima (mockup .m-drawer) */}
      <div
        className={clsx(
          'transform transition-all duration-300 ease-in-out',
          'hidden min-[900px]:flex min-[900px]:flex-col static inset-0 w-64',
          isOpen ? 'min-[900px]:w-64' : 'min-[900px]:w-16',
        )}
        style={{
          backgroundColor: 'var(--theme-sidebar-background)',
          borderRight: '1px solid var(--theme-sidebar-border)',
        }}
        data-sidebar-root
      >
        <div className="flex flex-col h-full w-full">
          {/* Topo (HEADER_HEIGHT): logo + nome do workspace (estilo sb-brand). Rail = só logo. */}
          <div
            className={clsx('flex items-center', expanded ? 'gap-2 px-3 justify-between' : 'justify-center px-2')}
            style={{
              borderBottom: '1px solid var(--theme-sidebar-border)',
              height: HEADER_HEIGHT,
            }}
          >
            <Link to="/" className={clsx('flex items-center gap-2 min-w-0', expanded && 'flex-1')}>
              <span className="flex items-center justify-center flex-shrink-0 transition-transform duration-500 ease-in-out hover:rotate-[360deg]">
                {app?.logo ? (
                  <img src={app.logo} alt={app.name || 'Logo'} className="h-[34px] w-auto" />
                ) : (
                  <span
                    className="h-[34px] w-[34px] rounded-md flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: 'var(--theme-primary)',
                      color: 'var(--theme-sidebar-active-text)',
                    }}
                  >
                    {app?.name?.charAt(0)?.toUpperCase() || 'T'}
                  </span>
                )}
              </span>
              {expanded && (
                <span className="flex flex-col min-w-0 leading-tight">
                  <span
                    className="text-sm font-bold tracking-[-0.02em] truncate min-w-0"
                    style={{ color: 'var(--theme-sidebar-text-hover)' }}
                  >
                    {app?.name || 'TROIA'}
                  </span>
                  {(() => {
                    // `.sb-brand .txt .s` do mockup: subtítulo 11px muted (workspace/empresa)
                    const companyName = savedSessions.find(s => s.email === state.user?.email)?.companyName;
                    return companyName ? (
                      <span
                        className="text-[11px] truncate min-w-0"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {companyName}
                      </span>
                    ) : null;
                  })()}
                </span>
              )}
            </Link>
            {expanded && onToggle && (
              <button
                type="button"
                onClick={onToggle}
                className="w-[32px] h-[32px] flex-shrink-0 flex items-center justify-center rounded-lg transition-colors"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                aria-label="Colapsar menu"
                data-testid="sidebar-collapse"
              >
                <Icon name="panel-left" className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>

          {/* Busca / ⌘K — affordance visível do shell (abre o CommandPalette). Só no modo expandido (mockup §3.1 .ds-input-search). */}
          {expanded && (
            <div className="px-3 pt-3">
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('troia:open-command-palette'))}
                data-testid="sidebar-search"
                aria-label="Buscar"
                className="flex items-center gap-2 w-full h-[38px] px-3 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--theme-input-background)',
                  border: '1px solid var(--theme-input-border)',
                  color: 'var(--theme-muted-text)',
                }}
              >
                <Icon name="search" className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 min-w-0 text-left text-sm truncate">Buscar</span>
                <span
                  className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: 'var(--theme-button-ghost-hover)', color: 'var(--theme-muted-text)' }}
                >
                  ⌘K
                </span>
              </button>
            </div>
          )}

          {/* Toolbar de atalhos (abaixo do header) — util-row do shell, agora no topo. Expandido = bloco levinho com gaps iguais; rail = coluna. Colapsar vive no header (expandido) / expandir ACIMA da toolbar (rail). */}
          <div className={clsx(expanded ? 'px-3 pt-3' : 'px-2 pt-2')}>
            {/* Expandir — FORA do bloco (só rail), ACIMA dos 6 utilitários (posição pedida pelo dono) */}
            {!expanded && onToggle && (
              <div className="flex justify-center pb-1.5">
                <button
                  type="button"
                  onClick={onToggle}
                  className="w-[32px] h-[32px] flex items-center justify-center rounded-lg transition-colors"
                  style={{ color: 'var(--theme-sidebar-text)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
                    showTooltip(e, 'Expandir menu');
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    hideTooltip();
                  }}
                  aria-label="Expandir menu"
                >
                  <Icon name="panel-left" className="h-[18px] w-[18px]" />
                </button>
              </div>
            )}
            <div
              className={clsx('flex items-center rounded-[10px]', expanded ? 'flex-row justify-between' : 'flex-col gap-1.5')}
              style={{ backgroundColor: 'rgba(148,163,184,0.09)', padding: expanded ? '5px 6px' : '6px 0' }}
              data-testid="sidebar-toolbar"
            >
              {/* Mensagens (chat) — badge violeta. Só com chat:read (mesma regra da tela). */}
              {canAccessPath('/chat') && (
              <Link
                to="/chat"
                className="w-[32px] h-[32px] flex items-center justify-center rounded-lg relative transition-colors"
                data-testid="sidebar-util-mensagens"
                style={{
                  color: location.pathname.startsWith('/chat')
                    ? 'var(--theme-sidebar-item-active-text)'
                    : 'var(--theme-sidebar-text)',
                  backgroundColor: location.pathname.startsWith('/chat')
                    ? 'var(--theme-sidebar-item-active-bg)'
                    : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (!location.pathname.startsWith('/chat')) {
                    e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
                  }
                  showTooltip(e, `Mensagens${totalUnreadCount > 0 ? ` (${totalUnreadCount})` : ''}`);
                }}
                onMouseLeave={(e) => {
                  if (!location.pathname.startsWith('/chat')) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                  hideTooltip();
                }}
                aria-label="Mensagens"
              >
                <Icon name="message-square" className="h-[18px] w-[18px]" />
                {totalUnreadCount > 0 && (
                  <span
                    className="absolute flex items-center justify-center rounded-full text-white font-bold"
                    style={{
                      top: -2, right: -2, minWidth: 16, height: 16,
                      padding: '0 4px', fontSize: 9, lineHeight: 1,
                      backgroundColor: 'var(--theme-primary)',
                    }}
                  >
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </span>
                )}
              </Link>
              )}
              {/* Notificações */}
              <div onMouseEnter={(e) => showTooltip(e, 'Notificações')} onMouseLeave={hideTooltip}>
                <NotificationsDropdown />
              </div>
              {/* Atividades */}
              <div onMouseEnter={(e) => showTooltip(e, 'Atividades')} onMouseLeave={hideTooltip}>
                <ActivitiesDropdown />
              </div>
              {/* Chat de equipe */}
              <div onMouseEnter={(e) => showTooltip(e, 'Chat da equipe')} onMouseLeave={hideTooltip}>
                <TeamChatButton />
              </div>
              {/* E-mail — caixa de conversas de e-mail, exige chat:read */}
              {canAccessPath('/email') && (
              <button
                type="button"
                onClick={() => navigate('/email')}
                className="w-[32px] h-[32px] flex items-center justify-center rounded-lg relative transition-colors"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
                  showTooltip(e, 'E-mail');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  hideTooltip();
                }}
                aria-label="E-mail"
              >
                <Icon name="mail" className="h-[18px] w-[18px]" />
                {emailUnreadCount > 0 && (
                  <span
                    className="absolute flex items-center justify-center rounded-full text-white font-bold"
                    style={{
                      top: -2, right: -2, minWidth: 16, height: 16,
                      padding: '0 4px', fontSize: 9, lineHeight: 1,
                      backgroundColor: '#ef4444',
                    }}
                  >
                    {emailUnreadCount > 99 ? '99+' : emailUnreadCount}
                  </span>
                )}
              </button>
              )}
              {/* Tema */}
              <button
                type="button"
                onClick={toggleTheme}
                className="w-[32px] h-[32px] flex items-center justify-center rounded-lg transition-colors"
                style={{ color: 'var(--theme-sidebar-text)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
                  showTooltip(e, theme === 'light' ? 'Tema escuro' : 'Tema claro');
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  hideTooltip();
                }}
                aria-label={theme === 'light' ? 'Tema escuro' : 'Tema claro'}
              >
                <Icon name={theme === 'light' ? 'moon' : 'sun'} className="h-[18px] w-[18px]" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className={clsx('flex-1 py-2 overflow-y-auto overflow-x-hidden', {
            'px-2': expanded,
            'px-1': !expanded,
          })}>
            {filteredSections.map((section, sectionIndex) => (
              <div key={section.label || `section-${sectionIndex}`}>
                {renderSectionLabel(section.label)}
                <div className={clsx({ 'space-y-0.5': expanded, 'space-y-1.5': !expanded })}>
                  {section.items.map(item => renderMenuItem(item))}
                </div>
              </div>
            ))}
          </nav>

          {/* Settings Menu - Fixed above Messages */}
          {(() => {
            const configItem = filteredSettingsItem;
            if (!configItem) return null;

            const hasChildren = configItem.children && configItem.children.length > 0;
            const isActive = isItemActive(configItem);
            const isCompact = !expanded;

            return (
              <div
                className={clsx('pt-2', {
                  'px-3 pb-1': expanded,
                  'px-1 pb-1': !expanded,
                })}
                style={{ borderTop: '1px solid var(--theme-sidebar-border)' }}
              >
                {/* Settings button */}
                <div
                  ref={settingsTriggerRef}
                  className={clsx(
                    'flex items-center text-sm rounded-lg cursor-pointer transition-all duration-200 group relative',
                    (isActive || openDropdownId === configItem.id) ? 'font-semibold' : 'font-medium',
                    {
                      'h-[38px] px-3': !isCompact,
                      'w-[32px] h-[32px] mx-auto justify-center': isCompact,
                    }
                  )}
                  style={{
                    backgroundColor: (isActive || openDropdownId === configItem.id)
                      ? 'var(--theme-sidebar-item-active-bg)'
                      : 'transparent',
                    color: (isActive || openDropdownId === configItem.id)
                      ? 'var(--theme-sidebar-item-active-text)'
                      : 'var(--theme-sidebar-text)',
                  }}
                  onMouseEnter={(e) => {
                    if (isCompact) showTooltip(e, configItem.label);
                    if (!isActive && openDropdownId !== configItem.id) {
                      e.currentTarget.style.color = 'var(--theme-sidebar-text-hover)';
                      e.currentTarget.style.backgroundColor = 'rgba(148,163,184,0.14)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isCompact) hideTooltip();
                    if (!isActive && openDropdownId !== configItem.id) {
                      e.currentTarget.style.color = 'var(--theme-sidebar-text)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                  onClick={() => {
                    hideTooltip();
                    if (hasChildren) {
                      toggleDropdown(configItem.id);
                    }
                  }}
                >
                  {configItem.iconName && (
                    <Icon
                      name={configItem.iconName as IconName}
                      className={clsx('h-[18px] w-[18px] flex-shrink-0', {
                        'mr-3': !isCompact,
                      })}
                    />
                  )}

                  {!isCompact && (
                    <>
                      {configItem.href && !hasChildren ? (
                        <Link to={configItem.href} className="flex-1">
                          {configItem.label}
                        </Link>
                      ) : (
                        <span className="flex-1">{configItem.label}</span>
                      )}

                      {hasChildren && (
                        <div className="ml-auto">
                          <Icon name="chevron-right" className="h-4 w-4" style={{ opacity: 0.5 }} />
                        </div>
                      )}
                    </>
                  )}

                  {/* Link wrapper for compact mode */}
                  {isCompact && configItem.href && !hasChildren && (
                    <Link to={configItem.href} className="absolute inset-0" />
                  )}
                </div>
              </div>
            );
          })()}

          {/* Rodapé: bloco de conta (os utilitários vivem na toolbar do topo agora) */}
          <div className={clsx('pb-2 pt-1', expanded ? 'px-2' : 'px-1')}>

            {/* Bloco de conta (perfil + menu com Cobrança/Instalar/Ajuda). Rail = avatar-only */}
            <div className={clsx('mt-1', !expanded && 'flex justify-center')}>
              <UserProfileDropdown compact={!expanded} />
            </div>
          </div>
        </div>
      </div>

      {/* Settings dropdown */}
      {filteredSettingsItem?.children && (
        <SidebarDropdown
          items={filteredSettingsItem.children}
          title="Configurações"
          direction="up"
          triggerEl={settingsTriggerRef.current}
          isOpen={openDropdownId === filteredSettingsItem.id}
          onClose={closeDropdown}
        />
      )}

      {/* Company Edit Drawer (opened from Settings > Empresa) */}
      <CompanyEditDrawer
        isOpen={companyDrawerOpen}
        onClose={closeCompanyDrawer}
      />

      {/* All Sessions Dialog (sub-tela de conta do drawer mobile) */}
      <AllSessionsDialog
        open={showAllSessions}
        onOpenChange={setShowAllSessions}
        sessions={otherSessions}
        onSessionSelect={(session) => { void switchTo(session); }}
        onSessionDelete={removeSession}
      />

      {/* Portal tooltip (escapa overflow-x-hidden / stacking). 'up' quando expandido, 'right' no rail. */}
      {tooltip && createPortal(
        tooltip.dir === 'down' ? (
          <div
            style={{
              position: 'fixed',
              top: tooltip.top,
              left: tooltip.left,
              zIndex: 9999,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {/* Arrow pointing up */}
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: '6px solid var(--theme-sidebar-active)',
                marginLeft: 8,
                flexShrink: 0,
              }}
            />
            {/* Tooltip body */}
            <div
              style={{
                backgroundColor: 'var(--theme-sidebar-active)',
                color: '#ffffff',
                padding: '6px 11px',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: '7px',
                whiteSpace: 'nowrap',
              }}
            >
              {tooltip.label}
            </div>
          </div>
        ) : tooltip.dir === 'up' ? (
          <div
            style={{
              position: 'fixed',
              top: tooltip.top,
              left: tooltip.left,
              transform: 'translate(-50%, -100%)',
              zIndex: 9999,
              pointerEvents: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* Tooltip body */}
            <div
              style={{
                backgroundColor: 'var(--theme-sidebar-active)',
                color: '#ffffff',
                padding: '6px 11px',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: '7px',
                whiteSpace: 'nowrap',
              }}
            >
              {tooltip.label}
            </div>
            {/* Arrow pointing down */}
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: '6px solid var(--theme-sidebar-active)',
                flexShrink: 0,
              }}
            />
          </div>
        ) : (
          <div
            style={{
              position: 'fixed',
              top: tooltip.top,
              left: tooltip.left,
              transform: 'translateY(-50%)',
              zIndex: 9999,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {/* Arrow pointing left */}
            <div
              style={{
                width: 0,
                height: 0,
                borderTop: '6px solid transparent',
                borderBottom: '6px solid transparent',
                borderRight: '6px solid var(--theme-sidebar-active)',
                flexShrink: 0,
              }}
            />
            {/* Tooltip body */}
            <div
              style={{
                backgroundColor: 'var(--theme-sidebar-active)',
                color: '#ffffff',
                padding: '6px 11px',
                fontSize: '13px',
                fontWeight: 500,
                borderRadius: '7px',
                whiteSpace: 'nowrap',
              }}
            >
              {tooltip.label}
            </div>
          </div>
        ),
        document.body
      )}
    </>
  );
};